import React from 'react';
import {UserInsights} from "types/User";
import StatList from "components/StatList";
import StatBoxSimple from "components/StatBoxSimple";
import Divider from "components/Divider";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import {toTitleCase} from "services/string";
import FeedbackStars from "components/FeedbackStars";
import {ThumbDownIcon, ThumbUpIcon} from "@heroicons/react/solid";
import colors from "tailwindcss/colors";
import {formattedDateTime} from "services/date";
import Card from "components/Card";
import {useQuery} from "react-query";
import ngage from "services/ngage";

// Types ----

type FeedbackCardProps = {
  userId: number
  insights?: UserInsights | null
}

// Component ----

function FeedbackCard({userId, insights}: FeedbackCardProps) {

  const {data: feedback, isLoading: isLoadingFeedback} = useQuery(
    ['feedback.all', {authors: [userId]}],
    () => ngage.feedback.matching({authors: [userId]}),
    {keepPreviousData: true}
  )

  return (
    <Card heading="Feedback" isLoading={isLoadingFeedback}>
      <StatList columns={2}>
        <StatBoxSimple name='Total' stat={insights?.feedback.all_feedback}/>
      </StatList>

      {!feedback?.data?.length ? (
        <div className="mt-6 flow-root">
          <Divider/>
          <p className="text-sm text-gray-400 mt-6">No feedback</p>
        </div>
      ) : (
        <div className="mt-6">
          <StackedList>
            {feedback?.data?.map((feedback) => (
              <StackedListItem key={feedback.id}>
                <p className="truncate text-sm font-medium text-gray-500">{feedback.subject?.name}</p>

                <div
                  className="mt-4 grid grid-cols-1 gap-1 font-normal text-sm text-gray-900">
                  <p>
                    <span className="mr-1.5 text-gray-400">Type:</span>
                    {toTitleCase(feedback.subject_type)}
                  </p>
                  <div className="flex">
                    <p className="mr-1.5 text-gray-400">Rating:</p>

                    {feedback.type === 'customer-satisfaction' && <FeedbackStars score={feedback.score ?? 0}/>}


                    {feedback.type === 'like-dislike' && (
                      <>
                        {feedback.score ?
                          <ThumbUpIcon className="w-5" color={colors.green[600]}/> :
                          <ThumbDownIcon className="w-5" color={colors.red[600]}/>
                        }
                      </>
                    )}
                  </div>
                  {feedback.comments && (
                    <p>
                      <span className="mr-1.5 text-gray-400">Comments:</span>
                      {feedback.comments}
                    </p>
                  )}

                  <p>
                    <span className="mr-1.5 text-gray-400">Feedback left:</span>
                    {formattedDateTime(feedback.created_at)}
                  </p>
                </div>
              </StackedListItem>
            ))}
          </StackedList>
        </div>
      )}
    </Card>
  );
}

// Export ----

export default FeedbackCard;